import axios from '@/js/helper/axios-helper.js'
import { get, isUndefined } from 'lodash-es'
import { generateCompareProductData } from '@/js/helper/product-data-helper'
import API_CONSTANTS from '@/js/helper/api-constants.js'
import { STORE_MODULES, APP_GETTER_TYPES, REQUEST_STATE, TYPES } from './std-imports'
import { DP_ROLE } from '@/js/helper/dp-usermanagement-constants'

export const getCompareApiActions = MUTATION_TYPES => ({
  [TYPES.SET_COMPARE_URL]: ({ state, commit, dispatch }, url) => {
    if (get(state, MUTATION_TYPES.COMPARE.urlKey) === url) {
      return
    }
    commit(MUTATION_TYPES.COMPARE.urlKey, url)
    return dispatch(TYPES.COMPARE_REQUEST)
  },
  [TYPES.COMPARE_REQUEST]: async (
    { state, commit, getters, dispatch, rootGetters },
    {
      method = 'GET',
      data,
      url,
      include = ['media', 'price', 'attribute', 'text'],
      requestAfterParams,
      fixedAfterRequestState = undefined,
    } = {},
  ) => {
    if (
      !rootGetters[`${STORE_MODULES.APP}/${APP_GETTER_TYPES.HAS_ROLE}`](DP_ROLE.SHOP_COMPARE_R.p)
    ) {
      return Promise.resolve({})
    }

    if (!url && !state[MUTATION_TYPES.COMPARE.urlKey]) {
      return Promise.resolve({})
    }
    if (
      getters[`${MUTATION_TYPES.COMPARE.BASE}_${REQUEST_STATE.STATE_PENDING}`] &&
      !isUndefined(requestAfterParams)
    ) {
      await state[MUTATION_TYPES.COMPARE.promiseKey]
    }

    const finalUrl = url || state[MUTATION_TYPES.COMPARE.urlKey]
    const resultUrl = include
      ? `${finalUrl}${/\?/.test(finalUrl) ? '&' : '?'}ai%5Binclude%5D=${encodeURIComponent(
          include,
        )}`
      : finalUrl

    const params = {
      method,
      url: resultUrl,
    }

    if (data) {
      params.data = data
    }

    const promise = axios(params)
      .then(response => {
        if (!isUndefined(requestAfterParams)) {
          return dispatch(TYPES.COMPARE_REQUEST, requestAfterParams)
        }

        const transformedData = generateCompareProductData({
          apiData: response.data,
        })

        if (fixedAfterRequestState) {
          commit(MUTATION_TYPES.COMPARE.BASE, {
            type: REQUEST_STATE.STATE_SUCCESS,
            data: fixedAfterRequestState,
            status: response.status,
          })
        } else {
          commit(MUTATION_TYPES.COMPARE.BASE, {
            type: REQUEST_STATE.STATE_SUCCESS,
            data: transformedData,
            status: response.status,
          })
        }

        return response.data
      })
      .catch(error => {
        const status = get(error, 'response.status', 400)

        if (requestAfterParams) {
          return dispatch(TYPES.COMPARE_REQUEST, requestAfterParams)
        }

        commit(MUTATION_TYPES.COMPARE.BASE, {
          type: REQUEST_STATE.STATE_FAILURE,
          data: {},
          status: status,
          errorMessage: error.message,
        })

        return Promise.reject(new Error(error.message))
      })

    commit(MUTATION_TYPES.COMPARE.BASE, { type: REQUEST_STATE.STATE_PENDING, promise })

    return promise
  },
  [TYPES.COMPARE_ADD_PRODUCT]: async (
    { dispatch, state, rootGetters },
    { productId, scope, config = [], maxCount = 4 } = {},
  ) => {
    if (
      !rootGetters[`${STORE_MODULES.APP}/${APP_GETTER_TYPES.HAS_ROLE}`](DP_ROLE.SHOP_COMPARE_W.p)
    ) {
      return Promise.resolve({})
    }
    const currentCompareProductCount = get(
      state,
      [MUTATION_TYPES.COMPARE.dataKey, API_CONSTANTS.DATA],
      [],
    ).length

    if (currentCompareProductCount === maxCount) {
      return
    }

    const attributes = {
      'customer.lists.domain': 'product',
      'customer.lists.type': 'compare',
      'customer.lists.refid': productId,
    }

    if (config.length) {
      attributes['customer.lists.config'] = config
    }

    const baseUrl = state[MUTATION_TYPES.COMPARE.urlKey]
    const url = baseUrl.includes('&ai%5Bid%5D=')
      ? baseUrl
      : `${baseUrl}&ai%5Bid%5D=${productId}&ai%5BcompareScope%5D=${scope}`

    return dispatch(TYPES.COMPARE_REQUEST, {
      url,
      method: 'POST',
      data: {
        data: [
          {
            attributes,
          },
        ],
      },
    })
  },
  [TYPES.COMPARE_SWAP_PRODUCT]: async (
    { dispatch, rootGetters },
    { oldProductId, productId, afterDeleteState, scope },
  ) => {
    if (
      !rootGetters[`${STORE_MODULES.APP}/${APP_GETTER_TYPES.HAS_ROLE}`](DP_ROLE.SHOP_COMPARE_W.p)
    ) {
      return Promise.resolve({})
    }
    await dispatch(TYPES.COMPARE_REMOVE_PRODUCT, { productId: oldProductId, afterDeleteState })
    // Overwrite Max Count for ADD_PRODUCT because here we keep the 4 previous products in the compareData
    await dispatch(TYPES.COMPARE_ADD_PRODUCT, {
      productId,
      maxCount: 5,
      scope,
    })
  },
  [TYPES.COMPARE_REMOVE_PRODUCT]: async (
    { dispatch, state, rootGetters },
    { productId, afterDeleteState = undefined },
  ) => {
    if (
      !rootGetters[`${STORE_MODULES.APP}/${APP_GETTER_TYPES.HAS_ROLE}`](DP_ROLE.SHOP_COMPARE_W.p)
    ) {
      return Promise.resolve({})
    }
    const url = state[MUTATION_TYPES.COMPARE.urlKey]

    return dispatch(TYPES.COMPARE_REQUEST, {
      method: 'DELETE',
      url: `${url}${/\?/.test(url) ? '&' : '?'}ai%5Bid%5D=${productId}`,
      requestAfterParams: afterDeleteState ? undefined : {},
      fixedAfterRequestState: afterDeleteState,
    })
  },
})
